import { useCallback, useEffect, useState } from 'react'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { getCompanies, getProperties } from '@/features/invitee/services/inviteeService'
import { useUserRole } from '@/hooks/useUserRole'
import { showApiError } from '@/lib/utils/notifications'
import { ReportDateRangePicker } from './ReportDateRangePicker'
import { getSalesTeamsForPmcReport } from '../services/reportsService'
import type { SearchableSelectOption } from '@/components/ui/SearchableSelect'
import type { SalesTeamOption } from '../types/api'
import type {
  ConfiguredReportConfiguredReportFiltersProps,
  ConfiguredReportSelectFilterConfig,
} from '../types/pages'
import { INPUT_CLASS, LABEL_CLASS } from '../constants/configuredReportStyles'
import { selectOptionsForKind } from '../utils/configuredReportParams'

export function ConfiguredReportFilters({
  config,
  filters,
  isOpen,
  onClose,
  onApply,
  onReset,
}: ConfiguredReportConfiguredReportFiltersProps) {
  const { companyId, propertyId } = useUserRole()
  const lockedCompanyId =
    config.lockedValues?.company_id != null
      ? String(config.lockedValues.company_id)
      : companyId != null
        ? String(companyId)
        : undefined
  const lockedPropertyId =
    config.lockedValues?.property_id != null
      ? String(config.lockedValues.property_id)
      : propertyId != null
        ? String(propertyId)
        : undefined
  const [draft, setDraft] = useState<Record<string, unknown>>(filters)
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
  const [properties, setProperties] = useState<SearchableSelectOption[]>([])
  const [salesTeams, setSalesTeams] = useState<SalesTeamOption[]>([])

  const loadCompanies = useCallback(
    async (search: string) => {
      if (lockedCompanyId || (search && search.length < 2)) return
      const rows = await getCompanies(search)
      setCompanies(
        rows.map((row) => ({
          value: String(row.company_id),
          label: row.company_name,
        })),
      )
    },
    [lockedCompanyId],
  )

  const loadProperties = useCallback(
    async (search: string) => {
      if (lockedPropertyId || (search && search.length < 2)) return
      const listed = String(draft.listed ?? '1') === '0' ? '0' : '1'
      const rows = await getProperties(
        search,
        lockedCompanyId ||
          (draft.company_id
            ? String(draft.company_id)
            : draft.companyId
              ? String(draft.companyId)
              : undefined),
        undefined,
        listed as '0' | '1',
      )
      setProperties(
        rows.map((row) => ({
          value: String(row.property_id),
          label: row.property_name,
        })),
      )
    },
    [draft.companyId, draft.company_id, draft.listed, lockedCompanyId, lockedPropertyId],
  )

  /* eslint-disable react-hooks/set-state-in-effect -- Angular parity: sync draft and preload lookups when filter drawer opens */
  useEffect(() => {
    if (isOpen) setDraft(filters)
  }, [isOpen, filters])

  useEffect(() => {
    if (!isOpen) return
    void loadCompanies('')
    void loadProperties('')
    if (config.selectFilters?.some((filter) => filter.kind === 'salesTeam')) {
      getSalesTeamsForPmcReport().then(setSalesTeams).catch(showApiError)
    }
  }, [config.selectFilters, isOpen, loadCompanies, loadProperties])
  /* eslint-enable react-hooks/set-state-in-effect */

  const patch = (partial: Record<string, unknown>) =>
    setDraft((prev) => ({ ...prev, ...partial }))

  const companyOptions = lockedCompanyId
    ? [{ value: lockedCompanyId, label: lockedCompanyId }]
    : companies
  const propertyOptions = lockedPropertyId
    ? [{ value: lockedPropertyId, label: lockedPropertyId }]
    : properties
  const optionsForFilter = (filter: ConfiguredReportSelectFilterConfig) => {
    const options = selectOptionsForKind(
      filter.kind,
      companyOptions,
      propertyOptions,
      salesTeams,
    )
    if (filter.kind !== 'roleName') return options
    const companyMode = String(draft.pmc ?? '') === 'COMPANY'
    const allowed = new Set(
      companyMode
        ? ['ROLE_COMPANY_MANAGER', 'ROLE_COMPANY_ADMIN']
        : [
            'ROLE_PROPERTY_MANAGER',
            'ROLE_PROPERTY_ADMIN',
            'ROLE_PROPERTY_AGENT',
            'ROLE_REGIONAL_MANAGER',
          ],
    )
    return options.filter((option) => allowed.has(option.value))
  }

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={onReset}
      onDone={() => {
        onApply(draft)
        onClose()
      }}
      testIdPrefix={config.testIdPrefix}
    >
      <div className="space-y-4">
        {config.dateFilter ? (
          <div className="space-y-2">
            <label className={LABEL_CLASS}>{config.dateFilter.label}</label>
            <ReportDateRangePicker
              startDate={String(draft[config.dateFilter.fromKey] ?? '')}
              endDate={String(draft[config.dateFilter.toKey] ?? '')}
              onChange={(start, end) =>
                patch({
                  [config.dateFilter!.fromKey]: start
                    ? formatDateForAPI(start)
                    : '',
                  [config.dateFilter!.toKey]: end ? formatDateForAPI(end) : '',
                })
              }
            />
          </div>
        ) : null}

        {config.selectFilters
          ?.filter(
            (filter) =>
              !(
                (config.testIdPrefix === 'missing-contacts' &&
                  filter.kind === 'property' &&
                  String(draft.pmc ?? '') === 'COMPANY') ||
                (filter.kind === 'property' &&
                  String(draft.report_type ?? '') === 'COMPANY')
              ),
          )
          .map((filter) => (
            <div key={filter.key} className="space-y-2">
              <label className={LABEL_CLASS}>{filter.label}</label>
              <SearchableSelect
                options={optionsForFilter(filter)}
                value={
                  filter.multiple
                    ? Array.isArray(draft[filter.key])
                      ? (draft[filter.key] as string[])
                      : []
                    : String(draft[filter.key] ?? '')
                }
                onChange={(value) => {
                  const next = filter.multiple
                    ? Array.isArray(value)
                      ? value
                      : value
                        ? [value]
                        : []
                    : Array.isArray(value)
                      ? (value[0] ?? '')
                      : value
                  patch({
                    [filter.key]: next,
                    ...(filter.kind === 'company'
                      ? { property_id: '', propertyId: '' }
                      : {}),
                    ...(filter.kind === 'reportType'
                      ? { property_id: '', propertyId: '' }
                      : {}),
                    ...(filter.kind === 'pmc' ? { role_name: [] } : {}),
                  })
                  if (filter.kind === 'company') void loadProperties('')
                }}
                onSearch={
                  filter.kind === 'company' && !lockedCompanyId
                    ? loadCompanies
                    : filter.kind === 'property' && !lockedPropertyId
                      ? loadProperties
                      : undefined
                }
                placeholder={filter.placeholder}
                multiple={filter.multiple}
                disabled={
                  (filter.kind === 'company' && Boolean(lockedCompanyId)) ||
                  (filter.kind === 'property' && Boolean(lockedPropertyId))
                }
              />
            </div>
          ))}

        {config.textFilters?.map((filter) => (
          <div key={filter.key} className="space-y-2">
            <label className={LABEL_CLASS}>{filter.label}</label>
            <input
              className={INPUT_CLASS}
              value={String(draft[filter.key] ?? '')}
              onChange={(e) => patch({ [filter.key]: e.target.value })}
              placeholder={filter.placeholder}
            />
          </div>
        ))}
      </div>
    </ListFilterDrawer>
  )
}
